import React, { useEffect, useMemo, useState } from "react";
import RateMeCommentBlock from "./RateMeCommentBlock";
import RateMeReplyBlock from "./RateMeReplyBlock";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isRateMe(comment) {
  return (
    comment?.kind === "rate_me" ||
    comment?.type === "rate_me" ||
    Boolean(comment?.audio?.audio_url)
  );
}

function isRateMeReply(comment) {
  return (
    comment?.kind === "rate_me_reply" ||
    comment?.type === "rate_me_reply" ||
    Boolean(comment?.ratings?.length || comment?.sentenceRating)
  );
}

function getCommentId(comment) {
  return comment?.id || comment?._id;
}

function getAuthorName(comment) {
  return comment?.username || comment?.user || comment?.author || "User";
}

function isAudioLikeFile(value) {
  return /\.(mp3|wav|m4a|webm|ogg|aac)$/i.test(String(value || ""));
}

function getChapterDisplay(comment) {
  if (comment?.chapterTitle) return comment.chapterTitle;

  if (comment?.file && !isAudioLikeFile(comment.file)) {
    return comment.file;
  }

  return "";
}

function getSubjectAuthor(comment) {
  return comment?.subject?.author || comment?.subjectAuthor || "Unknown author";
}

function getSubjectLanguage(comment) {
  return comment?.subject?.language || comment?.language || "Unknown language";
}

function getKindLabel(comment) {
  if (isRateMe(comment)) return "Rate me";
  if (isRateMeReply(comment)) return "Verdict";
  return "Discussion";
}

function getKindStyle(comment) {
  if (isRateMe(comment)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (isRateMeReply(comment)) {
    return "border-green-200 bg-green-50 text-green-700";
  }

  return "border-blue-200 bg-blue-50 text-blue-700";
}

function formatDate(rawDate) {
  if (!rawDate) return "";

  const date = rawDate instanceof Date ? rawDate : new Date(rawDate);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSnippet(item) {
  const text =
    item?.content ||
    item?.text ||
    item?.comment ||
    item?.anchor?.text ||
    "";

  const cleaned = String(text).trim();

  if (!cleaned || cleaned === "Human pronunciation verdict") {
    if (isRateMe(item)) return "Asked for a human pronunciation assessment.";
    if (isRateMeReply(item)) return "Submitted a human pronunciation verdict.";
    return "No note added.";
  }

  return cleaned.length > 110 ? `${cleaned.slice(0, 110)}…` : cleaned;
}

function SelectFilter({ label, value, onChange, options, allLabel = "All" }) {
  return (
    <label className="flex min-w-[160px] flex-1 flex-col gap-1 sm:flex-none">
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
      >
        <option value="all">{allLabel}</option>

        {(options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;

  return (
    <div className="mt-5 flex items-center justify-center gap-3">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(Math.max(1, page - 1))}
        className={cx(
          "rounded-2xl border px-5 py-2.5 text-sm font-black shadow-sm transition",
          page <= 1
            ? "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50"
        )}
      >
        Previous
      </button>

      <span className="rounded-full bg-white px-4 py-2 text-sm font-black text-slate-500 shadow-sm">
        {page} / {totalPages}
      </span>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPage(Math.min(totalPages, page + 1))}
        className={cx(
          "rounded-2xl border px-5 py-2.5 text-sm font-black shadow-sm transition",
          page >= totalPages
            ? "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50"
        )}
      >
        Next
      </button>
    </div>
  );
}

function Pill({ children, className }) {
  if (!children) return null;

  return (
    <span
      className={cx(
        "inline-flex max-w-[220px] items-center truncate rounded-full border px-2.5 py-1 text-[11px] font-black",
        className || "border-slate-200 bg-slate-50 text-slate-500"
      )}
    >
      {children}
    </span>
  );
}

function MetricPill({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-600",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    green: "border-green-200 bg-green-50 text-green-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-black",
        tones[tone] || tones.slate
      )}
    >
      <span>{value}</span>
      <span className="font-bold opacity-75">{label}</span>
    </span>
  );
}

function Chevron({ open }) {
  return (
    <span
      className={cx(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border shadow-sm transition",
        open
          ? "rotate-90 border-blue-200 bg-blue-600 text-white shadow-blue-600/20"
          : "rotate-0 border-slate-200 bg-white text-slate-500"
      )}
    >
      ›
    </span>
  );
}

function CommentMeta({ comment }) {
  const chapterDisplay = getChapterDisplay(comment);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Pill>{getSubjectLanguage(comment)}</Pill>
      <Pill>{getSubjectAuthor(comment)}</Pill>
      <Pill>{chapterDisplay}</Pill>
      <Pill className={getKindStyle(comment)}>{getKindLabel(comment)}</Pill>
    </div>
  );
}

function ThreadHeader({ comment }) {
  const name = getAuthorName(comment);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-black text-white shadow-md shadow-blue-600/20">
            {String(name).charAt(0).toUpperCase()}
          </div>

          <div>
            <h3 className="truncate text-lg font-black text-slate-950">
              {name}
            </h3>

            <div className="text-xs font-bold text-slate-400">
              {formatDate(comment?.createdAt || comment?.created_at)}
            </div>
          </div>
        </div>

        <CommentMeta comment={comment} />
      </div>

      <div className="rounded-full bg-slate-50 px-3 py-1 text-xs font-black text-slate-500">
        Score {comment?.score || 0}
      </div>
    </div>
  );
}

function ActionRow({
  comment,
  editing,
  canEdit,
  canDelete,
  onStartEdit,
  onDelete,
}) {
  if (comment?.isDeleted || comment?.deleted) return null;

  const editable = canEdit?.(comment) && !editing && !isRateMe(comment);
  const deletable = canDelete?.(comment);

  if (!editable && !deletable) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {editable && (
        <button
          type="button"
          onClick={onStartEdit}
          className="text-sm font-black text-slate-500 transition hover:text-slate-950"
        >
          Edit
        </button>
      )}

      {deletable && (
        <button
          type="button"
          onClick={onDelete}
          className="text-sm font-black text-rose-500 transition hover:text-rose-700"
        >
          Delete
        </button>
      )}
    </div>
  );
}

function EditableCommentBody({
  comment,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}) {
  const initialContent =
    comment?.content || comment?.text || comment?.comment || "";

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(initialContent);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    setEditText(initialContent);
    setEditing(false);
  }, [initialContent, getCommentId(comment)]);

  if (comment?.isDeleted || comment?.deleted) {
    return (
      <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-bold italic text-slate-400">
        Deleted comment
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
        {editing ? (
          <>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full min-h-[110px] resize-y rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            />

            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setEditText(initialContent);
                }}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={savingEdit || !editText.trim()}
                onClick={async () => {
                  setSavingEdit(true);
                  await onEdit?.(getCommentId(comment), editText);
                  setSavingEdit(false);
                  setEditing(false);
                }}
                className={cx(
                  "rounded-xl px-5 py-2.5 text-sm font-black text-white",
                  savingEdit || !editText.trim()
                    ? "cursor-not-allowed bg-blue-300"
                    : "bg-blue-600 shadow-md shadow-blue-600/20 hover:bg-blue-700"
                )}
              >
                {savingEdit ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        ) : (
          <p className="whitespace-pre-wrap break-words text-[15px] font-medium leading-7 text-slate-900">
            {initialContent || "No content"}
          </p>
        )}
      </div>

      <ActionRow
        comment={comment}
        editing={editing}
        canEdit={canEdit}
        canDelete={canDelete}
        onStartEdit={() => setEditing(true)}
        onDelete={() => onDelete?.(getCommentId(comment))}
      />
    </>
  );
}

function GlobalCommentCard({
  comment,
  onRefresh,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  depth = 0,
}) {
  const replies = comment?.replies || [];

  const alreadyAnsweredByMe =
    isRateMe(comment) && Boolean(comment?.myRateMeReply);

  const payloadBase = {
    subject: comment?.subject,
    file: comment?.file,
    chapterTitle:
      getChapterDisplay(comment) || comment?.chapterTitle || comment?.file,
  };

  const anchor = comment?.anchor?.text
    ? {
        text: comment.anchor.text,
        start: comment.anchor.start,
        end: comment.anchor.end,
      }
    : null;

  return (
    <article
      className={cx(
        "rounded-[28px] border bg-white p-5 shadow-sm transition hover:shadow-md",
        depth > 0
          ? "border-slate-200"
          : isRateMe(comment)
          ? "border-violet-200"
          : "border-slate-200"
      )}
    >
      <ThreadHeader comment={comment} />

      {comment?.anchor?.text && !isRateMe(comment) && !isRateMeReply(comment) && (
        <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3">
          <div className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-blue-500">
            Referenced text
          </div>

          <div className="line-clamp-2 text-sm italic leading-6 text-slate-600">
            “{comment.anchor.text}”
          </div>
        </div>
      )}

      {isRateMe(comment) && !comment?.isDeleted ? (
        <>
          <RateMeCommentBlock
            comment={comment}
            content={comment?.content || ""}
            canInteract={Boolean(localStorage.getItem("token"))}
            payloadBase={payloadBase}
            anchor={anchor}
            onSubmitted={onRefresh}
            alreadyAnswered={alreadyAnsweredByMe}
          />

          <ActionRow
            comment={comment}
            editing={false}
            canEdit={canEdit}
            canDelete={canDelete}
            onStartEdit={null}
            onDelete={() => onDelete?.(getCommentId(comment))}
          />
        </>
      ) : isRateMeReply(comment) && !comment?.isDeleted ? (
        <>
          <RateMeReplyBlock reply={comment} />

          <ActionRow
            comment={comment}
            editing={false}
            canEdit={null}
            canDelete={canDelete}
            onStartEdit={null}
            onDelete={() => onDelete?.(getCommentId(comment))}
          />
        </>
      ) : (
        <EditableCommentBody
          comment={comment}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}

      {replies.length > 0 && (
        <div className="mt-5 space-y-3 border-l-2 border-slate-100 pl-4">
          {replies.map((reply, index) => (
            <GlobalCommentCard
              key={getCommentId(reply) || index}
              comment={reply}
              onRefresh={onRefresh}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </article>
  );
}

function LatestPreview({ latest }) {
  if (!latest) return null;

  return (
    <div className="mt-3 min-w-0 text-sm">
      <span className="font-black text-slate-700">
        Latest: {latest.username || "User"}
      </span>
      <span className="mx-2 text-slate-300">·</span>
      <span className="font-bold text-slate-400">
        {formatDate(latest.updatedAt)}
      </span>

      <div className="mt-1 line-clamp-1 font-semibold text-slate-500">
        {getSnippet(latest)}
      </div>
    </div>
  );
}

function SubjectPreview({ subject, open }) {
  return (
    <div
      className={cx(
        "mt-3 rounded-2xl border px-4 py-3 transition",
        open
          ? "border-blue-200 bg-white shadow-sm"
          : "border-slate-100 bg-slate-50/70"
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <MetricPill label="threads" value={subject.threadCount || 0} tone="blue" />

        {(subject.rateMeCount || 0) > 0 && (
          <MetricPill
            label="Rate me"
            value={subject.rateMeCount}
            tone="violet"
          />
        )}

        <Pill>{subject.subjectAuthor}</Pill>
        <Pill>{subject.language}</Pill>

        {open && <MetricPill label="open" value="Now" tone="green" />}
      </div>

      <LatestPreview latest={subject.latest} />
    </div>
  );
}

function SubjectSection({
  subject,
  sort,
  author,
  kind,
  onSummaryRefresh,
  forceOpenToken,
  forceClosedToken,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSubjectThreads = async (nextPage = page) => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      const url = `${process.env.REACT_APP_USER_API_URL}/comments/community/subject?page=${nextPage}&pageSize=${pageSize}&sort=${sort}&language=${encodeURIComponent(
        subject.language || "all"
      )}&subjectTitle=${encodeURIComponent(
        subject.subjectTitle
      )}&author=${encodeURIComponent(author)}&kind=${encodeURIComponent(kind)}`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        throw new Error(`Subject threads ${res.status}`);
      }

      const json = await res.json();

      setItems(json.items || []);
      setPagination(json);
      setPage(json.page || nextPage);
    } catch (err) {
      console.error("fetch subject threads failed:", err);
      setError("Could not load this subject.");
    } finally {
      setLoading(false);
    }
  };

  const refreshThisSubject = async () => {
    await fetchSubjectThreads(page);
    await onSummaryRefresh?.();
  };

  const deleteAndRefresh = async (commentId) => {
    await onDelete?.(commentId);
    await refreshThisSubject();
  };

  const editAndRefresh = async (commentId, content) => {
    await onEdit?.(commentId, content);
    await refreshThisSubject();
  };

  useEffect(() => {
    if (forceOpenToken > 0) setOpen(true);
  }, [forceOpenToken]);

  useEffect(() => {
    if (forceClosedToken > 0) setOpen(false);
  }, [forceClosedToken]);

  useEffect(() => {
    setOpen(false);
    setItems([]);
    setPagination(null);
    setPage(1);
  }, [subject.subjectTitle, subject.language, author, kind, sort]);

  useEffect(() => {
    if (open && items.length === 0) {
      fetchSubjectThreads(1);
    }
  }, [open]);

  return (
    <section
      className={cx(
        "overflow-hidden rounded-[30px] border bg-white shadow-sm transition-all duration-200",
        open
          ? "border-blue-300 ring-4 ring-blue-100/80 shadow-xl shadow-blue-100/60"
          : "border-slate-200 hover:border-slate-300 hover:shadow-md"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cx(
          "flex w-full items-start justify-between gap-4 p-4 text-left transition sm:p-5",
          open
            ? "bg-gradient-to-br from-blue-50 via-white to-violet-50/50"
            : "hover:bg-slate-50/70"
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-xl font-black text-slate-950">
              {subject.subjectTitle}
            </h3>

            {open && (
              <span className="rounded-full border border-blue-200 bg-blue-600 px-3 py-1 text-xs font-black text-white shadow-sm shadow-blue-600/20">
                Expanded
              </span>
            )}
          </div>

          <SubjectPreview subject={subject} open={open} />
        </div>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="border-t border-blue-100 bg-gradient-to-b from-blue-50/40 via-slate-50/60 to-white p-4 sm:p-5">
          <div className="mb-4 rounded-2xl border border-blue-100 bg-white/80 px-4 py-3 text-sm font-bold text-slate-600 shadow-sm">
            Showing threads only for{" "}
            <span className="font-black text-slate-950">
              {subject.subjectTitle}
            </span>
            . Pagination below applies to this subject only.
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-5 py-10 text-center text-sm font-bold text-slate-500">
              Loading threads…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-5 py-10 text-center text-sm font-bold text-slate-500">
              No threads found.
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((comment, index) => (
                <GlobalCommentCard
                  key={getCommentId(comment) || index}
                  comment={comment}
                  onRefresh={refreshThisSubject}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={editAndRefresh}
                  onDelete={deleteAndRefresh}
                />
              ))}
            </div>
          )}

          <Pagination
            page={page}
            totalPages={pagination?.totalPages || 1}
            onPage={(nextPage) => fetchSubjectThreads(nextPage)}
          />
        </div>
      )}
    </section>
  );
}

function LanguagePreview({ language }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <MetricPill
        label="subjects"
        value={language.subjects?.length || 0}
        tone="slate"
      />

      <MetricPill
        label="threads"
        value={language.threadCount || 0}
        tone="blue"
      />

      {(language.rateMeCount || 0) > 0 && (
        <MetricPill
          label="Rate me"
          value={language.rateMeCount}
          tone="violet"
        />
      )}

      {language.latest && (
        <span className="inline-flex min-w-0 max-w-full rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-500">
          <span className="shrink-0 text-slate-400">Latest&nbsp;</span>
          <span className="truncate">
            {language.latest.username || "User"} ·{" "}
            {formatDate(language.latest.updatedAt)}
          </span>
        </span>
      )}
    </div>
  );
}

function LanguageSection({
  language,
  sort,
  author,
  kind,
  onSummaryRefresh,
  forceOpenToken,
  forceClosedToken,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpenToken > 0) setOpen(true);
  }, [forceOpenToken]);

  useEffect(() => {
    if (forceClosedToken > 0) setOpen(false);
  }, [forceClosedToken]);

  useEffect(() => {
    setOpen(false);
  }, [language.language, author, kind, sort]);

  const subjects = useMemo(() => {
    return [...(language.subjects || [])].sort((a, b) => {
      const aTime = a.latestAt ? new Date(a.latestAt).getTime() : 0;
      const bTime = b.latestAt ? new Date(b.latestAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [language.subjects]);

  return (
    <section
      className={cx(
        "overflow-hidden rounded-[36px] border bg-white/80 shadow-sm transition",
        open ? "border-blue-200 shadow-lg shadow-blue-100/50" : "border-slate-200"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cx(
          "flex w-full items-start justify-between gap-4 p-5 text-left transition sm:p-6",
          open
            ? "bg-gradient-to-br from-blue-50 via-white to-slate-50"
            : "bg-gradient-to-br from-white via-slate-50 to-blue-50/40 hover:from-slate-50 hover:to-blue-50"
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
            Language
          </div>

          <h2 className="mt-1 text-2xl font-black text-slate-950">
            {language.language}
          </h2>

          <LanguagePreview language={language} />
        </div>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="space-y-4 border-t border-slate-100 bg-white p-4 sm:p-5">
          {subjects.map((subject) => (
            <SubjectSection
              key={`${subject.language}-${subject.subjectTitle}`}
              subject={subject}
              sort={sort}
              author={author}
              kind={kind}
              onSummaryRefresh={onSummaryRefresh}
              forceOpenToken={forceOpenToken}
              forceClosedToken={forceClosedToken}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function CommunityCommentsBrowser({ auth, successHandler }) {
  const [languages, setLanguages] = useState([]);
  const [facets, setFacets] = useState({
    languages: [],
    authors: [],
    subjects: [],
    kinds: [],
  });

  const [language, setLanguage] = useState("all");
  const [author, setAuthor] = useState("all");
  const [subjectTitle, setSubjectTitle] = useState("all");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("active");

  const [totalThreads, setTotalThreads] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState("");

  const [forceOpenToken, setForceOpenToken] = useState(0);
  const [forceClosedToken, setForceClosedToken] = useState(0);

  const token = localStorage.getItem("token");

  const currentUsername =
    auth?.username ||
    auth?.user?.username ||
    auth?.currentUser?.username ||
    localStorage.getItem("username") ||
    null;

  const currentUserId =
    auth?.userId ||
    auth?.id ||
    auth?._id ||
    auth?.user?.id ||
    auth?.user?._id ||
    auth?.currentUser?.id ||
    auth?.currentUser?._id ||
    localStorage.getItem("userId") ||
    null;

  const currentUserRole =
    auth?.role ||
    auth?.user?.role ||
    auth?.currentUser?.role ||
    localStorage.getItem("role") ||
    null;

  const canEditComment = (comment) => {
    if (comment?.isDeleted || comment?.deleted) return false;

    const usernameMatches =
      Boolean(currentUsername) && comment?.username === currentUsername;

    const userIdMatches =
      Boolean(currentUserId) && String(comment?.userId) === String(currentUserId);

    return Boolean(usernameMatches || userIdMatches);
  };

  const canDeleteComment = (comment) => {
    if (comment?.isDeleted || comment?.deleted) return false;

    const usernameMatches =
      Boolean(currentUsername) && comment?.username === currentUsername;

    const userIdMatches =
      Boolean(currentUserId) && String(comment?.userId) === String(currentUserId);

    return Boolean(usernameMatches || userIdMatches || currentUserRole === "ADMIN");
  };

  const updateComment = async (commentId, content) => {
    if (!token || !commentId || !content.trim()) return;

    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${commentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ content: content.trim() }),
        }
      );

      if (!res.ok) {
        throw new Error(`Update ${res.status}`);
      }
    } catch (err) {
      console.error("update community comment failed:", err);
      setError("Could not update comment.");
    }
  };

  const deleteComment = async (commentId) => {
    if (!token || !commentId) return;

    const ok = window.confirm(
      "Delete this comment? Replies will stay visible in the thread."
    );

    if (!ok) return;

    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${commentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Delete ${res.status}`);
      }
    } catch (err) {
      console.error("delete community comment failed:", err);
      setError("Could not delete comment.");
    }
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    setError("");

    try {
      const url = `${process.env.REACT_APP_USER_API_URL}/comments/community/summary?language=${encodeURIComponent(
        language
      )}&author=${encodeURIComponent(author)}&subjectTitle=${encodeURIComponent(
        subjectTitle
      )}&kind=${encodeURIComponent(kind)}`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        throw new Error(`Community summary ${res.status}`);
      }

      const json = await res.json();

      setLanguages(json.languages || []);
      setFacets(json.facets || {});
      setTotalThreads(json.totalThreads || 0);
      setForceClosedToken((value) => value + 1);
    } catch (err) {
      console.error("fetch community summary failed:", err);
      setError("Could not load community comments.");
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [language, author, subjectTitle, kind]);

  const sortedLanguages = useMemo(() => {
    return [...languages].sort((a, b) => {
      const aTime = a.latestAt ? new Date(a.latestAt).getTime() : 0;
      const bTime = b.latestAt ? new Date(b.latestAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [languages]);

  const resetFilters = () => {
    setLanguage("all");
    setAuthor("all");
    setSubjectTitle("all");
    setKind("all");
    setSort("active");
    setForceClosedToken((value) => value + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 overflow-hidden rounded-[36px] border border-slate-200 bg-white shadow-sm">
          <div className="relative border-b border-slate-100 bg-gradient-to-br from-white via-blue-50/50 to-violet-50/40 p-6 sm:p-8">
            <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-blue-300/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-52 w-52 rounded-full bg-violet-300/20 blur-3xl" />

            <div className="relative flex flex-wrap items-start justify-between gap-5">
              <div>
                <button
                  type="button"
                  onClick={() => successHandler?.(false)}
                  className="mb-5 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
                >
                  <span>←</span>
                  <span>Back to library</span>
                </button>

                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                  Community
                </div>

                <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                  Community comments
                </h1>

                <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
                  Ask for human assessments, share tips, discuss tricky parts,
                  and learn together with fellow learners.
                </p>
              </div>

              <div className="rounded-[28px] bg-slate-950 px-6 py-5 text-white shadow-xl shadow-slate-950/10">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-white/50">
                  Threads
                </div>

                <div className="mt-1 text-4xl font-black">{totalThreads}</div>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              <SelectFilter
                label="Language"
                value={language}
                onChange={setLanguage}
                options={facets.languages}
              />

              <SelectFilter
                label="Author"
                value={author}
                onChange={setAuthor}
                options={facets.authors}
              />

              <SelectFilter
                label="Subject"
                value={subjectTitle}
                onChange={setSubjectTitle}
                options={facets.subjects}
              />

              <SelectFilter
                label="Type"
                value={kind}
                onChange={setKind}
                options={facets.kinds}
              />

              <label className="flex min-w-[160px] flex-1 flex-col gap-1 sm:flex-none">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                  Sort
                </span>

                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                >
                  <option value="active">Recently active</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="ranking">Top ranked</option>
                </select>
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-bold text-slate-400">
                {loadingSummary
                  ? "Loading…"
                  : `${totalThreads} thread${totalThreads === 1 ? "" : "s"} found`}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setForceOpenToken((value) => value + 1)}
                  className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-black text-blue-700 shadow-sm transition hover:bg-blue-100"
                >
                  Expand all
                </button>

                <button
                  type="button"
                  onClick={() => setForceClosedToken((value) => value + 1)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Collapse all
                </button>

                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Reset filters
                </button>
              </div>
            </div>
          </div>
        </header>

        {error && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        {loadingSummary ? (
          <div className="rounded-[32px] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-2xl">
              💬
            </div>
            <div className="text-base font-bold text-slate-500">
              Loading community comments…
            </div>
          </div>
        ) : sortedLanguages.length === 0 ? (
          <div className="rounded-[32px] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-2xl">
              🔎
            </div>

            <div className="text-base font-bold text-slate-500">
              No comments found.
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedLanguages.map((languageItem) => (
              <LanguageSection
                key={languageItem.language}
                language={languageItem}
                sort={sort}
                author={author}
                kind={kind}
                onSummaryRefresh={fetchSummary}
                forceOpenToken={forceOpenToken}
                forceClosedToken={forceClosedToken}
                canEdit={canEditComment}
                canDelete={canDeleteComment}
                onEdit={updateComment}
                onDelete={deleteComment}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}